import { useState, useEffect, useCallback, useMemo } from 'react';
import { supplierPmConfigApi } from '../services/supplierPmConfigApi';
import type { SupplierRow, SupplierPmAssignment, EmployeeRow, SupplierBusinessUnit } from '../types/supplierPmConfig';

export interface SupplierWithAssignments extends SupplierRow {
    businessUnits: string[];
    assignments: SupplierPmAssignment[];
}

interface UseSupplierPmConfigReturn {
    suppliers: SupplierRow[];
    assignments: SupplierPmAssignment[];
    employees: EmployeeRow[];
    companyCodes: string[];
    businessUnitCodes: string[];
    selectedCompanyCode: string | null;
    setSelectedCompanyCode: (code: string | null) => void;
    selectedBusinessUnit: string | null;
    setSelectedBusinessUnit: (code: string | null) => void;
    searchTerm: string;
    setSearchTerm: (term: string) => void;
    filteredSuppliers: SupplierWithAssignments[];
    loading: boolean;
    error: string | null;
    assign: (companyCode: string, supplierDimID: string, staffID: string, isPrimary?: boolean) => Promise<void>;
    unassign: (assignmentId: number) => Promise<void>;
    refresh: () => Promise<void>;
}

export function useSupplierPmConfig(): UseSupplierPmConfigReturn {
    const [suppliers, setSuppliers] = useState<SupplierRow[]>([]);
    const [supplierBusinessUnits, setSupplierBusinessUnits] = useState<SupplierBusinessUnit[]>([]);
    const [assignments, setAssignments] = useState<SupplierPmAssignment[]>([]);
    const [employees, setEmployees] = useState<EmployeeRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedCompanyCode, setSelectedCompanyCode] = useState<string | null>(null);
    const [selectedBusinessUnit, setSelectedBusinessUnit] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const [supplierData, employeeData] = await Promise.all([
                supplierPmConfigApi.getSuppliers(),
                supplierPmConfigApi.getEmployees(),
            ]);
            setSuppliers(supplierData.suppliers);
            setSupplierBusinessUnits(supplierData.supplierBusinessUnits);
            setAssignments(supplierData.assignments);
            setEmployees(employeeData.employees);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load supplier configuration');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const companyCodes = useMemo(() => {
        const codes = new Set(suppliers.map(s => s.CompanyCode));
        return Array.from(codes).sort();
    }, [suppliers]);

    // Default-select the first available company once data is loaded
    useEffect(() => {
        if (selectedCompanyCode === null && companyCodes.length > 0) {
            setSelectedCompanyCode(companyCodes[0]);
        }
    }, [companyCodes, selectedCompanyCode]);

    const assignmentsBySupplier = useMemo(() => {
        const map = new Map<string, SupplierPmAssignment[]>();
        for (const a of assignments) {
            const key = `${a.CompanyCode}::${a.SupplierDimID}`;
            const list = map.get(key) ?? [];
            list.push(a);
            map.set(key, list);
        }
        return map;
    }, [assignments]);

    const businessUnitsBySupplier = useMemo(() => {
        const map = new Map<string, string[]>();
        for (const bu of supplierBusinessUnits) {
            const key = `${bu.CompanyCode}::${bu.SupplierDimID}`;
            const list = map.get(key) ?? [];
            if (!list.includes(bu.BusinessUnitCode)) list.push(bu.BusinessUnitCode);
            map.set(key, list);
        }
        for (const list of map.values()) list.sort();
        return map;
    }, [supplierBusinessUnits]);

    const businessUnitCodes = useMemo(() => {
        const codes = new Set(supplierBusinessUnits.map(b => b.BusinessUnitCode));
        return Array.from(codes).sort();
    }, [supplierBusinessUnits]);

    const filteredSuppliers = useMemo<SupplierWithAssignments[]>(() => {
        const term = searchTerm.trim().toLowerCase();
        return suppliers
            .filter(s => !selectedCompanyCode || s.CompanyCode === selectedCompanyCode)
            .filter(s => {
                if (!selectedBusinessUnit) return true;
                const key = `${s.CompanyCode}::${s.SupplierDimID}`;
                return businessUnitsBySupplier.get(key)?.includes(selectedBusinessUnit) ?? false;
            })
            .filter(s => {
                if (!term) return true;
                return (
                    (s.SupplierDimName ?? '').toLowerCase().includes(term) ||
                    s.SupplierDimCode.toLowerCase().includes(term)
                );
            })
            .map(s => {
                const key = `${s.CompanyCode}::${s.SupplierDimID}`;
                return {
                    ...s,
                    businessUnits: businessUnitsBySupplier.get(key) ?? [],
                    assignments: assignmentsBySupplier.get(key) ?? [],
                };
            });
    }, [suppliers, selectedCompanyCode, selectedBusinessUnit, searchTerm, assignmentsBySupplier, businessUnitsBySupplier]);

    const assign = useCallback(async (companyCode: string, supplierDimID: string, staffID: string, isPrimary = false) => {
        await supplierPmConfigApi.assign(companyCode, supplierDimID, staffID, isPrimary);
        await fetchData();
    }, [fetchData]);

    const unassign = useCallback(async (assignmentId: number) => {
        await supplierPmConfigApi.unassign(assignmentId);
        await fetchData();
    }, [fetchData]);

    return {
        suppliers,
        assignments,
        employees,
        companyCodes,
        businessUnitCodes,
        selectedCompanyCode,
        setSelectedCompanyCode,
        selectedBusinessUnit,
        setSelectedBusinessUnit,
        searchTerm,
        setSearchTerm,
        filteredSuppliers,
        loading,
        error,
        assign,
        unassign,
        refresh: fetchData,
    };
}
